/**
 * Firebase Configuration
 *
 * SDK Strategy:
 * - Compat SDK: Primary SDK for all application features
 *   Use this for all standard Firestore operations, authentication, and storage.
 *   Import pattern: import { db } from 'path/to/firebase' or import app from 'path/to/firebase'
 *
 * - Modular SDK: Reserved for AI/experimental features only (lazy-loaded)
 *   Use getModularDb(), getModularAuth() when specifically needed for Firebase AI Logic.
 *   These are loaded on-demand to reduce initial bundle size.
 *
 * Usage:
 * - Services in src/api/firestore/services/ should import { db } from '../firebase'
 * - Legacy database files in src/database/ import app and create db locally
 * - All services should use firebase.firestore(app) pattern for consistency
 *
 * @module firebase
 */

import firebase from "firebase/compat/app";
import brandConfig from "./config/brandConfig";

// Lazy load Firebase modules to reduce initial bundle size
// Core auth and firestore are loaded immediately, analytics and storage are lazy-loaded
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";

// Analytics is loaded on-demand
let analyticsLoaded = false;

// Modular SDK imports for Firebase AI Logic - lazy loaded
let modularApp = null;
let modularAuth = null;
let modularDb = null;

// Firebase configuration from environment variables
// IMPORTANT: Never commit actual API keys to version control
const firebaseConfig = {
	apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
	authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
	projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
	storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
	appId: process.env.REACT_APP_FIREBASE_APP_ID,
	measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Validate required Firebase configuration
const requiredKeys = ['apiKey', 'authDomain', 'projectId'];
for (const key of requiredKeys) {
	if (!firebaseConfig[key]) {
		console.error(`[firebase] Missing required config: REACT_APP_FIREBASE_${key.toUpperCase().replace(/([A-Z])/g, '_$1')}`);
		throw new Error(`Missing required Firebase configuration: ${key}. Please check your environment variables.`);
	}
}

// Initialize compat SDK (existing functionality)
const app = firebase.initializeApp(firebaseConfig);
const db = app.firestore();

// Optional: Force long-polling for Firestore Listen streams.
// This helps in networks/browsers where QUIC/WebChannel gets blocked/flaky and can
// manifest as ERR_QUIC_PROTOCOL_ERROR and/or accumulating outstanding requests.
if (process.env.REACT_APP_FIRESTORE_FORCE_LONG_POLLING === "true") {
	try {
		db.settings({ experimentalForceLongPolling: true });
		console.log("[firebase] Firestore long-polling enabled");
	} catch (error) {
		console.warn("[firebase] Could not enable Firestore long-polling:", error);
	}
}

// Connect to Firebase Emulators in development/test mode
const isJest = process.env.NODE_ENV === "test" || !!process.env.JEST_WORKER_ID;
if (process.env.REACT_APP_USE_EMULATORS === "true" && !isJest) {
	console.log('[firebase] Connecting to Firebase Emulators...');
	const authPort = Number(process.env.REACT_APP_AUTH_EMULATOR_PORT || 9099);
	const firestorePort = Number(process.env.REACT_APP_FIRESTORE_EMULATOR_PORT || 8080);
	const storagePort = Number(process.env.REACT_APP_STORAGE_EMULATOR_PORT || 9199);

	// Connect Auth to emulator
	app.auth().useEmulator(`http://localhost:${authPort}`);
	console.log(`[firebase] Auth emulator connected at localhost:${authPort}`);

	// Connect Firestore to emulator
	db.useEmulator('localhost', firestorePort);
	console.log(`[firebase] Firestore emulator connected at localhost:${firestorePort}`);

	// Connect Storage to emulator
	try {
		app.storage().useEmulator('localhost', storagePort);
		console.log(`[firebase] Storage emulator connected at localhost:${storagePort}`);
	} catch (error) {
		console.warn('[firebase] Could not connect Storage emulator:', error);
	}
}

// Firebase Analytics initialization - will be conditionally initialized based on consent
let analyticsInstance = null;

/**
 * Initialize Firebase Analytics if consent is granted
 * @param {boolean} hasConsent - Whether analytics consent has been granted
 * @returns {Object|null} Analytics instance or null if consent not granted
 */
export const initializeAnalytics = async (hasConsent) => {
	if (hasConsent && !analyticsInstance) {
		try {
			// Lazy load analytics module
			await loadAnalytics();
			analyticsInstance = firebase.analytics();
			analyticsInstance.setUserProperties({
				brand: brandConfig.id,
				product_profile: brandConfig.productProfile,
			});
			console.log("[firebase][initializeAnalytics] - Firebase Analytics initialized");
			return analyticsInstance;
		} catch (error) {
			console.error("[firebase][initializeAnalytics] - Error initializing analytics:", error);
			return null;
		}
	}
	return analyticsInstance;
};

/**
 * Disable Firebase Analytics
 */
export const disableAnalytics = () => {
	if (analyticsInstance) {
		// Note: Firebase Analytics doesn't have a direct disable method
		// We prevent further tracking by not calling analytics methods
		console.log("[firebase][disableAnalytics] - Analytics tracking disabled");
		analyticsInstance = null;
	}
};

// Lazy load modular SDK (for AI Logic) - only when needed
export const getModularApp = async () => {
	if (!modularApp) {
		const { getApp } = await import("firebase/app");
		// Reuse the existing [DEFAULT] app initialized by the compat SDK.
		// Calling initializeApp() again would throw "already exists" in Firebase v9+
		// where compat and modular SDKs share the same app registry.
		modularApp = getApp();
	}
	return modularApp;
};

export const getModularAuth = async () => {
	if (!modularAuth) {
		const { getAuth } = await import("firebase/auth");
		const app = await getModularApp();
		modularAuth = getAuth(app);
	}
	return modularAuth;
};

export const getModularDb = async () => {
	if (!modularDb) {
		const { getFirestore } = await import("firebase/firestore");
		const app = await getModularApp();
		modularDb = getFirestore(app);
	}
	return modularDb;
};

// Lazy load storage
export const getStorage = async () => {
	return firebase.storage();
};

// Lazy load analytics
export const loadAnalytics = async () => {
	if (!analyticsLoaded) {
		await import("firebase/compat/analytics");
		analyticsLoaded = true;
	}
};

export const auth = app.auth();
export default app;
export { db };
